import { Stack } from '@chakra-ui/react';
import { BannerAspectRation, HeroImageProps, src } from '@/src/components/UI/HeroImageProps';
import Image from 'next/image';
import Head from 'next/head';
import React, { useCallback, useMemo, useState } from 'react';
import { Property } from 'csstype';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Autoplay } from 'swiper';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';
import { isServer } from '@/src/libs/is-next-server';
import ObjectFit = Property.ObjectFit;

const BANNER_TOOLTIP_TEXT = '建案欣賞';

const Images: Partial<{ [key in src]: string }> = {
  Home: `${process.env.NEXT_PUBLIC_GOOGLE_CDN_URL}/mobile/largeImages/hero-img-mobile/home_v_4.webp`,
  Sell: `${process.env.NEXT_PUBLIC_GOOGLE_CDN_URL}/mobile/largeImages/hero-img-mobile/hero-sell-mobile_v_3.webp`,
  // Served locally from /public — shared with the desktop build, see HeroImage.tsx.
  aboutUS: '/desktop/hero/about.webp',
  jobs: `${process.env.NEXT_PUBLIC_GOOGLE_CDN_URL}/mobile/largeImages/hero-img-mobile/hero-careers-mobile-v-2.webp`,
  agent: `${process.env.NEXT_PUBLIC_GOOGLE_CDN_URL}/mobile/largeImages/hero-img-mobile/hero-agents-mobile-v-3.webp`,
  abroad: '/desktop/hero/abroad.webp',
};

SwiperCore.use([Autoplay]);

const HeroImage = ({
  src,
  alt,
  text,
  action,
  mask = false,
  bannerSrc,
  bannerSets,
  bannerHref,
  bannerTitle,
  bannerAspectRation,
}: HeroImageProps) => {
  const [swiper, setSwiper] = useState<SwiperCore>();
  const [activeIndex, setActiveIndex] = useState(0);

  const onSlideChange = useCallback((item: SwiperCore) => {
    setActiveIndex(item.realIndex);
  }, []);

  const onBulletClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      const index = parseInt(e.currentTarget.dataset.slideIndex ?? '0');
      swiper?.slideToLoop(index);
    },
    [swiper]
  );

  return (
    <Stack spacing={0} w={'100%'} position={'relative'}>
      <div
        className={classNames(
          'w-full',
          {
            'pt-[calc((580/414)*100%)]': !bannerAspectRation?.mobile,
          },
          'z-[-1]'
        )}
        style={{
          aspectRatio: bannerAspectRation?.mobile
            ? `${bannerAspectRation.mobile.w}/${bannerAspectRation.mobile.h}`
            : undefined,
        }}
      />
      <Head>
        <link rel='preload' href={src ? Images[src] : bannerSrc} as='image' />
      </Head>
      <div className={'absolute inset-0'}>
        {mask && <div className={'absolute top-0 left-0 w-full h-full bg-black z-10 bg-opacity-25 pointer-events-none'} />}
        {bannerSets ? (
          <Swiper
            onSwiper={setSwiper}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            loop={bannerSets.length > 1}
            onSlideChange={onSlideChange}
          >
            {bannerSets.map(({ src, href, content, mask: slideMask }, index) => (
              <SwiperSlide key={index} className={classNames('w-full h-full')}>
                <BannerSetImageWrapper href={href}>
                  <BannerImage src={src} alt={alt} aspectRatio={bannerAspectRation?.mobile} />
                </BannerSetImageWrapper>
                <BannerSlideOverlay mask={slideMask}>{content}</BannerSlideOverlay>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <BannerSetImageWrapper
            href={bannerHref}
            className={'absolute inset-0'}
            tooltip={bannerHref ? BANNER_TOOLTIP_TEXT : undefined}
          >
            <BannerImage
              src={bannerSrc ? bannerSrc : src ? Images[src]! ?? Images['Home'] : ''}
              alt={alt}
              aspectRatio={bannerAspectRation?.mobile}
            />
          </BannerSetImageWrapper>
        )}
      </div>
      {bannerHref && !isServer() && <ReactTooltip backgroundColor='black' place={'bottom'} />}
      {text ? (
        <div className={'absolute flex justify-center items-center w-full h-full top-0 z-20'}>
          <p className='text-4xl leading-normal text-center font-medium uppercase text-white'>主導自己的未來</p>
        </div>
      ) : null}
      {action}
      {bannerTitle && <HeroBannerTitle title={bannerTitle} />}
      {bannerSets && bannerSets?.length > 1 && (
        <div
          className={classNames(
            'absolute inset-x-0 bottom-0 z-30',
            'flex items-end justify-center',
            'space-x-2',
            'pb-6'
          )}
        >
          {bannerSets.map((_, index) => (
            <span
              key={index}
              className={classNames('w-2 h-2 bg-white', 'rounded-full', {
                'bg-opacity-50': index !== activeIndex,
                'bg-opacity-100': index === activeIndex,
              })}
              data-slide-index={index}
              onClick={onBulletClick}
            />
          ))}
        </div>
      )}
    </Stack>
  );
};
export default HeroImage;

const BannerImage: React.FC<{
  src: string;
  alt: string;
  aspectRatio: BannerAspectRation['mobile'];
}> = ({ src, alt, aspectRatio }) => {
  const [imageSize, setImageSize] = useState<{ naturalWidth: number; naturalHeight: number } | null>(null);

  const objectFit = useMemo<ObjectFit>(() => {
    // Default to cover so landscape banners fill the frame height from the
    // first paint (no letterbox flash / no getting stuck on contain before
    // the image dimensions are known). Only fall back to contain once we
    // confirm the source is a portrait image.
    let objectFit: ObjectFit = 'cover';
    if (imageSize) {
      if (imageSize.naturalHeight > imageSize.naturalWidth) {
        objectFit = 'contain';
      }
    }
    return objectFit;
  }, [imageSize]);

  return (
    <Image
      priority
      src={src}
      objectFit={objectFit}
      className={'bg-gray300'}
      layout={'responsive'}
      width={aspectRatio?.w ?? 414}
      height={aspectRatio?.h ?? 580}
      alt={alt}
      blurDataURL={
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNUrAcAAMcAouVPGYoAAAAASUVORK5CYII='
      }
      placeholder={'blur'}
      loading={'eager'}
      onLoadingComplete={setImageSize}
    />
  );
};

const BannerSetImageWrapper: React.FC<{ href?: string; className?: string; tooltip?: string }> = ({
  href,
  children,
  className = 'w-full h-full',
  tooltip,
}) => {
  // With an active href the banner is a link; without one (target off-shelf or
  // unset) it falls back to a plain, non-clickable div.
  if (href) {
    return (
      <a
        href={href}
        target={'_blank'}
        rel={'noreferrer noopener'}
        className={classNames(className)}
        data-tip={tooltip}
      >
        {children}
      </a>
    );
  } else {
    return <div className={classNames(className)}>{children}</div>;
  }
};

const HeroBannerTitle: React.FC<{ title: string }> = ({ title }) => (
  <p
    className={classNames(
      'absolute z-20 left-[24px] bottom-[24px] pointer-events-none',
      'text-base font-bold text-white'
    )}
  >
    {title}
  </p>
);

// Per-slide overlay. Non-interactive by default so clicks on empty space still reach the
// slide's own link and swiping keeps working; content opts its buttons back in.
const BannerSlideOverlay: React.FC<{ mask?: boolean }> = ({ mask, children }) => (
  <>
    {mask && <div className={'absolute inset-0 bg-black bg-opacity-40 pointer-events-none'} />}
    {children && <div className={'absolute inset-0 z-20 pointer-events-none'}>{children}</div>}
  </>
);
